import { apiClient } from './apiClient';
import { authService } from './authService';

const getUserId = () => {
  // First try to get CMMS user ID from localStorage (set by CmmsAuthContext)
  const cmmsUserStr = localStorage.getItem('cmmsUser');
  if (cmmsUserStr) {
    try {
      const cmmsUser = JSON.parse(cmmsUserStr);
      if (cmmsUser?.id) {
        return cmmsUser.id;
      }
    } catch (e) {
      console.warn('Failed to parse cmmsUser from localStorage');
    }
  }
  // Fallback to main user
  const user = authService.getCurrentUser();
  return user?.id || user?.userId || 1;
};

export const workOrderService = {
  // Get all work orders
  async getWorkOrders() {
    return apiClient.get(`/api/cmms/work-orders`);
  },

  // Get work orders by status
  async getWorkOrdersByStatus(status) {
    return apiClient.get(`/api/cmms/work-orders/status/${status}`);
  },

  // Get work orders by type
  async getWorkOrdersByType(type) {
    return apiClient.get(`/api/cmms/work-orders/type/${type}`);
  },

  // Get work orders by priority
  async getWorkOrdersByPriority(priority) {
    return apiClient.get(`/api/cmms/work-orders/priority/${priority}`);
  },

  // Get work orders by asset
  async getWorkOrdersByAsset(assetId) {
    return apiClient.get(`/api/cmms/work-orders/asset/${assetId}`);
  },

  // Get my assigned work orders
  async getMyAssignments() {
    const userId = getUserId();
    return apiClient.get(`/api/cmms/work-orders/my-assignments?userId=${userId}`);
  },

  // Get work order statistics
  async getStats() {
    return apiClient.get(`/api/cmms/work-orders/stats`);
  },

  // Get single work order by ID
  async getWorkOrderById(id) {
    return apiClient.get(`/api/cmms/work-orders/${id}`);
  },

  // Create new work order
  async createWorkOrder(data) {
    const createdBy = getUserId();
    return apiClient.post(`/api/cmms/work-orders?createdBy=${createdBy}`, data);
  },

  // Update work order
  async updateWorkOrder(id, data) {
    return apiClient.put(`/api/cmms/work-orders/${id}`, data);
  },

  // Update work order status
  async updateStatus(id, status, notes = null) {
    const userId = getUserId();
    let url = `/api/cmms/work-orders/${id}/status?status=${status}&userId=${userId}`;
    if (notes) {
      url += `&notes=${encodeURIComponent(notes)}`;
    }
    return apiClient.put(url);
  },

  // Assign work order
  async assignWorkOrder(id, assignedTo) {
    return apiClient.put(`/api/cmms/work-orders/${id}/assign?assignedTo=${assignedTo}`);
  },

  // Complete work order
  async completeWorkOrder(id, notes = null) {
    const userId = getUserId();
    let url = `/api/cmms/work-orders/${id}/complete?userId=${userId}`;
    if (notes) {
      url += `&notes=${encodeURIComponent(notes)}`;
    }
    return apiClient.post(url);
  },

  // Delete work order
  async deleteWorkOrder(id) {
    return apiClient.delete(`/api/cmms/work-orders/${id}`);
  },

  // Log parts used from inventory (deducts stock, updates work order parts cost)
  async useParts(id, parts) {
    const userId = getUserId();
    return apiClient.post(
      `/api/cmms/work-orders/${id}/use-parts?userId=${userId}`,
      parts
    );
  },
};

// Work Order Status options
export const WORK_ORDER_STATUSES = [
  { value: 'OPEN', label: 'Open', color: 'gray', bgClass: 'bg-gray-100 text-gray-800' },
  { value: 'ASSIGNED', label: 'Assigned', color: 'blue', bgClass: 'bg-blue-100 text-blue-800' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'yellow', bgClass: 'bg-yellow-100 text-yellow-800' },
  { value: 'ON_HOLD', label: 'On Hold', color: 'orange', bgClass: 'bg-orange-100 text-orange-800' },
  { value: 'PENDING_REVIEW', label: 'Pending Review', color: 'purple', bgClass: 'bg-purple-100 text-purple-800' },
  { value: 'COMPLETED', label: 'Completed', color: 'green', bgClass: 'bg-green-100 text-green-800' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'red', bgClass: 'bg-red-100 text-red-800' },
];

// Work Order Type options
export const WORK_ORDER_TYPES = [
  { value: 'CORRECTIVE', label: 'Corrective', description: 'Repair broken equipment' },
  { value: 'PREVENTIVE', label: 'Preventive', description: 'Scheduled maintenance' },
  { value: 'INSPECTION', label: 'Inspection', description: 'Regular inspection' },
  { value: 'PROJECT', label: 'Project', description: 'Installation, upgrade' },
  { value: 'COMPONENT_REPLACEMENT', label: 'Component Replacement', description: 'Replace asset component' },
];

// Work Order Priority options
export const WORK_ORDER_PRIORITIES = [
  { value: 'LOW', label: 'Low', color: 'green' },
  { value: 'MEDIUM', label: 'Medium', color: 'yellow' },
  { value: 'HIGH', label: 'High', color: 'orange' },
  { value: 'URGENT', label: 'Urgent', color: 'red' },
];
